import { useEffect, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import Modal from 'react-modal';
import { IoCloseOutline } from 'react-icons/io5';
import { IoIosArrowDropleft, IoIosArrowDropright } from 'react-icons/io';

//
import { getProductImages } from '../../../api/services/productService';

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const ImageContainer = styled.div`
  position: relative;
  width: 400px;
  height: 400px;
  overflow: hidden;
  cursor: zoom-in;

  @media (max-width: 768px) {
    width: 350px;
    height: 350px;
  }

  @media (max-width: 480px) {
    width: 280px;
    height: 280px;
  }
`;

const Thumbnail = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ZoomLens = styled.div`
  position: absolute;
  width: ${(props) => props.zoomSize}px;
  height: ${(props) => props.zoomSize}px;
  border: 2px solid #fff;
  overflow: hidden;
  background: url(${(props) => props.image}) no-repeat;
  background-size: ${(props) => props.zoomFactor * 100}%;
  left: ${(props) => props.x}px;
  top: ${(props) => props.y}px;
  background-position: ${(props) => props.bgX}% ${(props) => props.bgY}%;
  pointer-events: none;

  @media (max-width: 768px) {
    width: ${(props) => props.zoomSize * 0.8}px;
    height: ${(props) => props.zoomSize * 0.8}px;
  }

  @media (max-width: 480px) {
    width: ${(props) => props.zoomSize * 0.6}px;
    height: ${(props) => props.zoomSize * 0.6}px;
  }
`;

const ModalWrapper = styled(Modal)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(3px);
`;

const ModalContent = styled.div`
  position: relative;
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (max-width: 768px) {
    padding: 15px;
  }

  @media (max-width: 480px) {
    padding: 10px;
  }
`;

const ModalImage = styled.img`
  max-width: 50vw;
  max-height: 80vh;
  object-fit: contain;
  border-radius: 10px;
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.7);

  @media (max-width: 768px) {
    max-width: 80vw;
    max-height: 70vh;
  }

  @media (max-width: 480px) {
    max-width: 90vw;
    max-height: 60vh;
  }
`;

const ModalImageInfo = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 5px;
    align-items: flex-start;
  }
`;

const ModalText = styled.p`
  color: ${(props) => props.theme.colors.white};
  font-size: 12px;

  @media (max-width: 480px) {
    font-size: 10px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: -15px;
  right: 20px;
  background: transparent;
  border: none;
  cursor: pointer;

  @media (max-width: 768px) {
    top: -10px;
    right: 15px;
  }

  @media (max-width: 480px) {
    top: -8px;
    right: 10px;
  }
`;

const ArrowButton = styled.button`
  position: absolute;
  top: 50vh;
  background: transparent;
  border: none;
  cursor: pointer;
  ${(props) => (props.left ? 'left: 10px;' : 'right: 10px;')}

  @media (max-width: 768px) {
    ${(props) => (props.left ? 'left: 5px;' : 'right: 5px;')}
  }

  @media (max-width: 480px) {
    ${(props) => (props.left ? 'left: 2px;' : 'right: 2px;')}
  }
`;

const ImageGallery = styled.div`
  display: flex;
  gap: 10px;
  overflow-x: auto;
  padding: 15px 0;
  scroll-behavior: smooth;
  position: relative;
  width: 400px; /* Same width as main image viewer */
  max-width: 100%;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #007bff;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #0056b3;
  }

  /* Scroll indicators */
  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    width: 20px;
    pointer-events: none;
    z-index: 1;
    display: ${(props) => (props.$hideSideFade ? 'none' : 'block')};
  }

  &::before {
    left: 0;
    background: linear-gradient(to right, white, transparent);
  }

  &::after {
    right: 0;
    background: linear-gradient(to left, white, transparent);
  }

  @media (max-width: 768px) {
    width: 350px;
    gap: 8px;
  }

  @media (max-width: 480px) {
    width: 280px;
    gap: 6px;
  }
`;

const SidebarImage = styled.img`
  width: 120px; /* Larger width to show fewer images */
  height: auto;
  border-radius: 0.5rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  border: 2px solid ${(props) => (props.isActive ? '#007bff' : 'transparent')};
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
  }

  @media (max-width: 768px) {
    width: 100px;
  }

  @media (max-width: 480px) {
    width: 80px;
  }
`;

const GalleryArrow = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(0, 123, 255, 0.8);
  color: white;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  font-size: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  z-index: 2;

  ${(props) => (props.left ? 'left: -20px;' : 'right: -20px;')}

  &:hover {
    background: rgba(0, 123, 255, 1);
    transform: translateY(-50%) scale(1.1);
  }

  @media (max-width: 768px) {
    width: 35px;
    height: 35px;
    font-size: 18px;
    ${(props) => (props.left ? 'left: -17px;' : 'right: -17px;')}
  }

  @media (max-width: 480px) {
    width: 30px;
    height: 30px;
    font-size: 16px;
    ${(props) => (props.left ? 'left: -15px;' : 'right: -15px;')}
  }
`;

const ImageViewer = ({ product }) => {
  const theme = useTheme();
  const [imageList, setImageList] = useState([]);
  const [currentImage, setCurrentImage] = useState({
    idx: 0,
    src: '',
  });

  const [zoomPosition, setZoomPosition] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [useFallback, setUseFallback] = useState(false);
  const [isLoadingImages, setIsLoadingImages] = useState(false);
  const zoomFactor = 3; // 확대 배율
  const zoomSize = 400; // ZoomLens 크기

  // Responsive zoom size calculation
  const getResponsiveZoomSize = () => {
    if (window.innerWidth <= 480) {
      return zoomSize * 0.6;
    } else if (window.innerWidth <= 768) {
      return zoomSize * 0.8;
    }
    return zoomSize;
  };

  // Function to fetch product images from API
  const fetchProductImages = async () => {
    if (!product?.idStyle) return;

    setIsLoadingImages(true);
    try {
      const result = await getProductImages(product.idStyle);

      if (
        result &&
        result.is_success &&
        result.images &&
        Array.isArray(result.images) &&
        result.images.length > 0
      ) {
        const apiImages = result.images
          .sort((a, b) => a.showOrder - b.showOrder)
          .map(
            (img) => `${process.env.REACT_APP_FILE_UPLOAD_URL}${img.filepath1}`,
          );

        setImageList(apiImages);
        setCurrentImage({
          idx: 0,
          src: apiImages[0],
        });
        return;
      }
    } catch (error) {
      console.error('Error fetching product images:', error);
    } finally {
      setIsLoadingImages(false);
    }

    // Fallback to original logic if API fails or no images
    const imgUrl = product.image
      ? product.image.replace(' copy', '').replace(' SAP', '')
      : useFallback || !product.filepath1
        ? `${process.env.REACT_APP_FILE_UPLOAD_URL}/style/${product.idStyle.replace(/\//g, '-')}.jpg`
        : `${process.env.REACT_APP_FILE_UPLOAD_URL}${product.filepath1.replace(' copy', '').replace(' SAP', '')}`;

    const imgList = [imgUrl];
    setImageList(imgList);
    setCurrentImage({
      idx: 0,
      src: imgList[0],
    });
  };

  useEffect(() => {
    fetchProductImages();
  }, [product?.idStyle]);

  const handleMouseMove = (e) => {
    const container = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - container.left;
    const y = e.clientY - container.top;

    // ZoomLens가 Thumbnail 안에 있도록 제한
    const currentZoomSize = getResponsiveZoomSize();
    const limitedX = Math.max(
      0,
      Math.min(x, container.width - currentZoomSize),
    );
    const limitedY = Math.max(
      0,
      Math.min(y, container.height - currentZoomSize),
    );

    const bgX = (x / container.width) * 100;
    const bgY = (y / container.height) * 100;

    setZoomPosition({ x: limitedX, y: limitedY, bgX, bgY });
  };

  const handleMouseLeave = () => {
    setZoomPosition(null);
  };

  const handleImageError = (imageIndex) => {
    // If we have multiple images from API, try the next one
    if (imageList.length > 1 && imageIndex < imageList.length - 1) {
      const nextImageIndex = imageIndex + 1;
      setCurrentImage({
        idx: nextImageIndex,
        src: imageList[nextImageIndex],
      });
      return;
    }

    // Fallback to original logic if no more images or single image
    if (!useFallback && product.filepath1) {
      setUseFallback(true);
      fetchProductImages(); // Re-fetch with fallback
    }
  };

  const handlePrev = () => {
    if (currentImage.idx == 0) {
      setCurrentImage({
        idx: imageList.length - 1,
        src: imageList[imageList.length - 1],
      });
    } else {
      setCurrentImage({
        idx: currentImage.idx - 1,
        src: imageList[currentImage.idx - 1],
      });
    }
  };

  const handleNext = () => {
    if (currentImage.idx == imageList.length - 1) {
      setCurrentImage({
        idx: 0,
        src: imageList[0],
      });
      return;
    } else {
      setCurrentImage({
        idx: currentImage.idx + 1,
        src: imageList[currentImage.idx + 1],
      });
    }
  };

  return (
    <Container>
      {/* 썸네일 이미지 */}
      <ImageContainer
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onClick={() => setShowModal(true)}
      >
        {isLoadingImages ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: '#666',
              fontSize: '14px',
            }}
          >
            Loading images...
          </div>
        ) : (
          <Thumbnail
            src={currentImage.src}
            alt='Thumbnail'
            onError={() => handleImageError(currentImage.idx)}
          />
        )}
        {zoomPosition && (
          <ZoomLens
            x={zoomPosition.x}
            y={zoomPosition.y}
            bgX={zoomPosition.bgX}
            bgY={zoomPosition.bgY}
            image={currentImage.src}
            zoomFactor={zoomFactor}
            zoomSize={getResponsiveZoomSize()}
          />
        )}
      </ImageContainer>

      <ModalWrapper
        isOpen={showModal}
        onRequestClose={() => setShowModal(false)}
        shouldCloseOnOverlayClick
      >
        <ArrowButton left onClick={handlePrev}>
          <IoIosArrowDropleft
            size={'50px'}
            color={theme.colors.secondaryText}
          />
        </ArrowButton>
        <ArrowButton onClick={handleNext}>
          <IoIosArrowDropright
            size={'50px'}
            color={theme.colors.secondaryText}
          />
        </ArrowButton>
        <ModalContent>
          <CloseButton onClick={() => setShowModal(false)}>
            <IoCloseOutline size={'30px'} color={theme.colors.white} />
          </CloseButton>
          <ModalImage
            src={currentImage.src}
            alt='Zoomed View'
            onError={() => handleImageError(currentImage.idx)}
          />
          <ModalImageInfo>
            <ModalText>{product.idStyle}</ModalText>
            <ModalText>{`${currentImage.idx + 1} of ${imageList.length}`}</ModalText>
          </ModalImageInfo>
        </ModalContent>
      </ModalWrapper>

      {imageList.length >= 1 && (
        <div style={{ marginTop: '20px', position: 'relative' }}>
          <div
            style={{
              width: '400px',
              maxWidth: '100%',
              margin: '0 auto',
            }}
          >
            <ImageGallery className='image-gallery' $hideSideFade={showModal}>
              {imageList.map((item, idx) => {
                return (
                  <SidebarImage
                    src={item}
                    key={`product_img_${idx}`}
                    onError={() => handleImageError(idx)}
                    onClick={() => {
                      setCurrentImage({
                        idx: idx,
                        src: imageList[idx],
                      });
                    }}
                    isActive={idx === currentImage.idx}
                  />
                );
              })}
            </ImageGallery>
          </div>

          {/* Navigation arrows for slider */}
          {!showModal && imageList.length > 4 && (
            <>
              <GalleryArrow
                left
                onClick={() => {
                  const gallery = document.querySelector('.image-gallery');
                  if (gallery) {
                    gallery.scrollBy({ left: -300, behavior: 'smooth' });
                  }
                }}
              >
                ‹
              </GalleryArrow>
              <GalleryArrow
                onClick={() => {
                  const gallery = document.querySelector('.image-gallery');
                  if (gallery) {
                    gallery.scrollBy({ left: 300, behavior: 'smooth' });
                  }
                }}
              >
                ›
              </GalleryArrow>
            </>
          )}
        </div>
      )}
    </Container>
  );
};

export default ImageViewer;
